/**
 * FocusOverlayHost (TASK-714) — renders the topmost focus-stack workspace as a
 * modal overlay above the dimmed canvas, behind the dock. URL stays /dashboard;
 * the topmost popup is encoded as `?focus=workspace:entityId` so F5 restores it
 * and browser Back pops one level (history push per push).
 *
 * Esc and ✕ route through focusStore.requestClose() — a workspace with dirty
 * state may veto, and a veto leaves popup, stack, and URL untouched.
 */

import React, { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import {
  useFocusStore,
  parseFocusParam,
  focusParamOf,
  MAX_FOCUS_DEPTH,
  type FocusEntry,
} from '../../stores/focusStore';

export interface FocusWorkspaceProps {
  entityId: string;
  /** The workspace signals its entity is invalid/unauthorized/deleted. */
  onInvalid: () => void;
}

// Workspace registry — lazy so popups never weigh down the dashboard bundle.
// TASK-714 ships the meeting-prep pilot; KYC arrives in TASK-716, workbench in
// TASK-717 (behind TASK-738's shell).
const MeetingPrepFocus = lazy(() => import('../meeting/preparation/MeetingPrepFocus'));
const KYCWorkspaceFocus = lazy(() => import('../kyc/workspace/KYCWorkspaceFocus'));

const WORKSPACE_RENDERERS: Partial<
  Record<FocusEntry['workspace'], React.ComponentType<FocusWorkspaceProps>>
> = {
  meeting: MeetingPrepFocus,
  kyc: KYCWorkspaceFocus,
};

export const FocusOverlayHost: React.FC = () => {
  const stack = useFocusStore((s) => s.stack);
  const depthRejected = useFocusStore((s) => s.depthRejected);
  const [invalidFocus, setInvalidFocus] = useState(false);

  const top = stack.length > 0 ? stack[stack.length - 1] : null;

  // ── URL sync ─────────────────────────────────────────────────────────────
  // ponytail: search-only history writes go through window.history directly —
  // routing never depends on ?focus=, and the imperative form avoids the
  // navigate()/effect echo (a same-commit stale closure was clearing the param
  // right after boot). React Router keeps owning pathname navigation.
  const writeUrl = (want: string | null, mode: 'push' | 'replace') => {
    const params = new URLSearchParams(window.location.search);
    if ((params.get('focus') ?? null) === want) return;
    if (want) params.set('focus', want);
    else params.delete('focus');
    const qs = params.toString();
    const url = `${window.location.pathname}${qs ? `?${qs}` : ''}`;
    if (mode === 'push') window.history.pushState({}, '', url);
    else window.history.replaceState({}, '', url);
  };

  // Boot: consume ?focus= once (F5 restore / deep link).
  useEffect(() => {
    const raw = new URLSearchParams(window.location.search).get('focus');
    if (!raw) return;
    const entry = parseFocusParam(raw);
    if (!entry) {
      // Invalid value: clear the param, show a neutral state (no permission oracle).
      setInvalidFocus(true);
      writeUrl(null, 'replace');
      return;
    }
    useFocusStore.getState().push(entry);
  }, []);

  // Store → URL. Deps only TRIGGER the run; the store is read fresh so a
  // same-commit stale closure can never mis-sync.
  useEffect(() => {
    const s = useFocusStore.getState();
    const t = s.stack.length > 0 ? s.stack[s.stack.length - 1] : null;
    writeUrl(t ? focusParamOf(t) : null, t ? 'push' : 'replace');
  }, [top?.workspace, top?.entityId, stack.length]);

  // Browser Back/Forward: reconcile the store to the URL; a veto restores the URL.
  useEffect(() => {
    const onPop = () => {
      const raw = new URLSearchParams(window.location.search).get('focus');
      const s = useFocusStore.getState();
      const t = s.stack.length > 0 ? s.stack[s.stack.length - 1] : null;
      const want = t ? focusParamOf(t) : null;
      if ((raw ?? null) === want) return;
      if (!raw && t) {
        void s.requestClose().then((closed) => {
          if (!closed) writeUrl(focusParamOf(t), 'push');
        });
        return;
      }
      const entry = raw ? parseFocusParam(raw) : null;
      if (entry) s.push(entry);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Esc pops exactly one level (through the close request).
  useEffect(() => {
    if (!top) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        void useFocusStore.getState().requestClose();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [top]);

  // Depth-cap hint: visible, self-clearing.
  const [showDepthHint, setShowDepthHint] = useState(false);
  const firstRejection = useRef(true);
  useEffect(() => {
    if (firstRejection.current) {
      firstRejection.current = false;
      return;
    }
    setShowDepthHint(true);
    const t = setTimeout(() => setShowDepthHint(false), 4000);
    return () => clearTimeout(t);
  }, [depthRejected]);

  const handleInvalidEntity = () => {
    setInvalidFocus(true);
    void useFocusStore.getState().requestClose();
  };

  if (!top && !invalidFocus && !showDepthHint) return null;

  const Renderer = top ? WORKSPACE_RENDERERS[top.workspace] : undefined;

  return (
    <>
      {invalidFocus && (
        <div
          className="fixed bottom-20 left-1/2 z-30 -translate-x-1/2 rounded-lg border border-border bg-background px-4 py-2 text-sm text-muted-foreground shadow-lg"
          data-testid="focus-not-found"
        >
          That item isn't available.
          <button
            type="button"
            className="ml-3 underline decoration-dotted"
            onClick={() => setInvalidFocus(false)}
          >
            Dismiss
          </button>
        </div>
      )}
      {showDepthHint && (
        <div
          className="fixed bottom-20 left-1/2 z-50 -translate-x-1/2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800 shadow-lg"
          data-testid="focus-depth-hint"
        >
          {`${MAX_FOCUS_DEPTH} workspaces are already open — close one first.`}
        </div>
      )}
      {/* TASK-876: say the cap BEFORE it is hit. The message above fires only on the
          rejected open, so the limit was undocumented right up to the moment it stopped
          you — the advisor learned the rule by breaking it. This shows the count from one
          short of the cap, which is where knowing it starts to matter. */}
      {!showDepthHint && stack.length >= MAX_FOCUS_DEPTH - 1 && (
        <div
          className="fixed bottom-20 left-1/2 z-40 -translate-x-1/2 rounded-full border border-border bg-background/95 px-3 py-1 text-xs text-muted-foreground shadow"
          data-testid="focus-depth-counter"
        >
          {`${stack.length} of ${MAX_FOCUS_DEPTH} workspaces open`}
        </div>
      )}

      {top && (
        <div className="fixed inset-0 z-30" data-testid="focus-overlay" role="dialog" aria-modal>
          {/* Dimmed canvas beneath; clicking it asks the top to close. */}
          <div
            className="absolute inset-0 bg-black/40"
            data-testid="focus-backdrop"
            onClick={() => void useFocusStore.getState().requestClose()}
          />

          {/* Stack chips for entries beneath the topmost. */}
          {stack.length > 1 && (
            <div className="absolute right-6 top-4 z-10 flex gap-2">
              {stack.slice(0, -1).map((entry) => (
                <span
                  key={focusParamOf(entry)}
                  className="rounded-full border border-border bg-background/90 px-2.5 py-1 text-xs text-muted-foreground shadow"
                  data-testid="focus-stack-chip"
                >
                  ⧉ {entry.workspace} · {entry.entityId.slice(0, 12)}
                </span>
              ))}
            </div>
          )}

          {/* The workspace: above the dimmed canvas, below the dock (z-40). */}
          <div className="absolute inset-x-4 bottom-20 top-6 mx-auto flex max-w-6xl flex-col overflow-hidden rounded-xl border border-border bg-background shadow-2xl md:inset-x-10">
            <div className="flex shrink-0 items-center justify-between border-b border-border px-4 py-2">
              <span className="text-sm font-medium text-foreground">
                {top.workspace === 'meeting'
                  ? 'Meeting preparation'
                  : top.workspace === 'kyc'
                    ? 'KYC workspace'
                    : top.workspace}
              </span>
              <button
                type="button"
                onClick={() => void useFocusStore.getState().requestClose()}
                aria-label="Close workspace"
                data-testid="focus-close"
                className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-accent"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-auto">
              {Renderer ? (
                <Suspense
                  fallback={
                    <div className="flex h-full items-center justify-center text-muted-foreground">
                      <Loader2 className="h-6 w-6 animate-spin" />
                    </div>
                  }
                >
                  <Renderer entityId={top.entityId} onInvalid={handleInvalidEntity} />
                </Suspense>
              ) : (
                <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                  This workspace opens from its own page for now.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
